import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import MuiMenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { ComponentProps, useMemo, useState } from 'react';
import * as React from 'react';
import { useLocation, useResolvedPath } from 'react-router-dom';

import { UnstyledNavLink } from '@/components/navigation/UnstyledNavLink';
import { StyledCounter } from '@/components/notifications/StyledCounter/StyledCounter';
import { WarningIndicator } from '@/components/notifications/WarningIndicator/WarningIndicator';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { COLORS } from '@/styles/tokens/colors';
import { zIndexes } from '@/styles/zIndexes';

import { SIDEBAR_ANIMATION_TIMING_MS } from './sidebarConstants';

export type MenuItemVariant = 'small' | 'large';

function LinkComponent({
  external,
  to,
  onClick,
  ariaLabel,
  children,
}: React.PropsWithChildren<{
  to?: string;
  external?: boolean;
  onClick?: () => void;
  ariaLabel?: string;
}>) {
  if (external) {
    return (
      <a
        aria-label={ariaLabel}
        href={to}
        rel="noreferrer"
        target="_blank"
        style={{ textDecoration: 'none' }}
      >
        {children}
      </a>
    );
  } else if (onClick) {
    return (
      <a
        aria-label={ariaLabel}
        style={{ textDecoration: 'none' }}
        onClick={onClick}
      >
        {children}
      </a>
    );
  } else if (!to) {
    return <>{children}</>;
  }

  return (
    <UnstyledNavLink aria-label={ariaLabel} to={to}>
      {children}
    </UnstyledNavLink>
  );
}

function getMenuItemStyling(opts: {
  isMenuItemActive: boolean;
  isHovered: boolean;
  colorTheme: 'light' | 'dark';
  highlightWhenActive?: boolean;
}) {
  let background = undefined;

  if (opts.highlightWhenActive && opts.isMenuItemActive) {
    if (opts.colorTheme === 'dark') {
      background = COLORS.GRAY[600];
    } else {
      background = COLORS.GRAY[200];
    }
  }

  if (opts.colorTheme === 'dark') {
    if (opts.isHovered) {
      background = COLORS.GRAY[600];
    }

    return {
      background,
      content: COLORS.PRIMITIVES.WHITE,
      iconColor: COLORS.PRIMITIVES.WHITE,
    };
  }

  if (opts.isHovered) {
    background = COLORS.GRAY[200];
  }

  return {
    background,
    content: COLORS.TEXT.PRIMARY,
    iconColor: COLORS.TEXT.PRIMARY,
  };
}

export interface MenuItemProps {
  groupName?: string; // groupName is used to display the group name in the sidebar
  text: string;
  icon: React.ReactElement;
  to?: string;
  // if the link is to an external route, we don't want to use the NavLink component.
  external?: boolean;
  variant: MenuItemVariant;
  onClick?: () => void;
  typographyProps?: ComponentProps<typeof Typography>;
  colorTheme?: 'light' | 'dark';
  highlightWhenActive?: boolean;
  menuItemSxProps?: ComponentProps<typeof MuiMenuItem>['sx'];
  isActive?: boolean;
  showWarningBadge?: boolean;
  /** A count to show in a badge on the icon. A count of 0 hides the badge. */
  badgeCount?: number;
  /** The accessible description of badgeCount, for example "2 new requests". */
  badgeLabel?: string;
  disabled?: boolean;
  showTooltip?: boolean;
  tooltipTitle?: string;
  tooltipPlacement?: ComponentProps<typeof Tooltip>['placement'];
  testId?: string;
}

/**
 * @description MenuItem is a menu component that's used for both the sidebar and the app bar (on top).
 */
export function MenuItem({
  text,
  icon,
  to,
  external,
  variant,
  onClick,
  typographyProps,
  colorTheme = 'dark',
  highlightWhenActive = true,
  menuItemSxProps,
  isActive,
  showWarningBadge,
  badgeCount = 0,
  badgeLabel,
  disabled,
  showTooltip,
  tooltipTitle = text,
  tooltipPlacement = 'right',
  testId,
}: MenuItemProps) {
  const path = useResolvedPath(to ?? '');
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);

  const warningIconProps = useMemo(() => {
    if (variant == 'small') {
      return {
        right: 0,
        top: 0,
      };
    }

    return {
      right: 10,
      top: 6,
    };
  }, [variant]);

  // i want to style the MenuItem component based on whether or not its active, rather than styling
  // the react-router NavLink component, so i have to derive this here. i use the same heuristic that
  // react-router uses for NavLinks: https://github.com/remix-run/react-router/blob/f3d87dcc91fbd6fd646064b88b4be52c15114603/packages/react-router-dom/index.tsx#L346
  // but loosened because we actually want it to also match every subpath of the top-level route
  // so this should return true for if the location.pathname is /households/<householdId>/whatever
  const isMenuItemActive = useMemo(() => {
    if (isActive === undefined) {
      return location.pathname.startsWith(path.pathname);
    }

    return isActive;
  }, [isActive, location.pathname, path.pathname]);
  const { background, content, iconColor } = getMenuItemStyling({
    isMenuItemActive,
    isHovered,
    colorTheme,
    highlightWhenActive,
  });

  const menuItem = (
    <Box data-testid={testId} sx={{ overflow: 'hidden', position: 'relative' }}>
      {showWarningBadge && (
        <Box
          sx={{
            zIndex: zIndexes.TOOLTIP,
            position: 'absolute',
            pointerEvents: 'none',
            ...warningIconProps,
          }}
        >
          <WarningIndicator size={16} />
        </Box>
      )}
      <LinkComponent
        ariaLabel={
          badgeCount > 0 && badgeLabel ? `${text}, ${badgeLabel}` : text
        }
        to={to}
        external={external}
        onClick={onClick}
      >
        <MuiMenuItem
          sx={{
            '&:hover': {
              backgroundColor: 'transparent', // Remove default hover effect
            },

            color: content,
            padding: '8px',
            borderRadius: 2,
            cursor: to || onClick ? 'pointer' : 'default',
            ...menuItemSxProps,
          }}
          style={{
            backgroundColor: background,
          }}
          disabled={disabled}
          selected={false}
          onMouseOver={() => setIsHovered(true)}
          onMouseOut={() => setIsHovered(false)}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <StyledCounter
              badgeContent={badgeCount}
              show={badgeCount > 0}
              color="error"
              max={9}
              aria-hidden
            >
              <Box sx={{ display: 'flex', color: iconColor }}>{icon}</Box>
            </StyledCounter>
            <Fade
              unmountOnExit={true}
              appear={false}
              timeout={{
                enter: SIDEBAR_ANIMATION_TIMING_MS,
                exit: SIDEBAR_ANIMATION_TIMING_MS / 2, // Exit faster to avoid content hanging around
              }}
              in={variant === 'large'}
            >
              <Typography
                color={content}
                variant="body-s"
                ml={1}
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                {...typographyProps}
              >
                {text}
              </Typography>
            </Fade>
          </Box>
        </MuiMenuItem>
      </LinkComponent>
    </Box>
  );

  // When tooltip is enabled, wrap menuItem in a forwardRef component
  if (showTooltip) {
    return (
      <Tooltip
        placement={tooltipPlacement}
        title={tooltipTitle}
        show={showTooltip}
      >
        <Box component="div">{menuItem}</Box>
      </Tooltip>
    );
  }

  return menuItem;
}
